import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Grid, IconButton, Snackbar, TextField, Tooltip, Typography } from "@mui/material";
import { AddCircle as IconAddCircle, Delete as IconDelete, Search as IconSearch } from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";

import * as configuracionService from "../../services/configuracionService";
import DataGridBase from "../../components/common/DataGridBase";
import DialogYesNo from "../../components/DialogYesNo";
import { useMantenimiento } from "../../hooks/useMantenimiento";

// Mantenimiento de los parametros del sistema (tabla configuracion).
//
// Aqui se enciende y apaga la bitacora de cambios: el parametro
// BITACORA_ACTIVA. El backend no deja borrar los parametros que lee el
// sistema, porque sin la fila su valor vuelve a ser "N" y nadie se entera.
const Index = () => {
    const { isReadOnly } = useMantenimiento("configuracion");
    const navigate = useNavigate();

    const [cargando, setCargando] = useState(true);
    const [results, setResults] = useState([]);
    const [filtro, setFiltro] = useState("");

    const [openDialogEliminar, setOpenDialogEliminar] = useState(false);
    const [parametroSeleccionado, setParametroSeleccionado] = useState(null);

    const [openMessage, setOpenMessage] = useState(false);
    const [propsMessage, setPropsMessage] = useState({ severity: "success", message: "" });

    const fetchResults = useCallback(async (texto) => {
        setCargando(true);

        const data = await configuracionService.getsConfiguracion()
            .catch((err) => { console.log("Err: ", err); return []; });

        // El grid pide un id unico por fila.
        let lista = (data || []).map((row) => ({ ...row, id: row.idConfiguracion }));

        const buscado = (texto ?? "").trim().toLowerCase();

        if (buscado.length > 0) {
            lista = lista.filter((row) =>
                (row.codigo || "").toLowerCase().includes(buscado)
                || (row.descripcion || "").toLowerCase().includes(buscado)
                || (row.valor || "").toLowerCase().includes(buscado));
        }

        setResults(lista);
        setCargando(false);
    }, [])

    useEffect(() => {
        fetchResults("");
    }, []);

    const mensaje = (severity, texto) => {
        setPropsMessage({ severity: severity, message: texto });
        setOpenMessage(true);
    }

    const eliminar = async () => {
        if (!parametroSeleccionado) return;

        const eliminado = await configuracionService.deleteConfiguracion(parametroSeleccionado.id)
            .then(() => true)
            .catch((err) => {
                console.log("Err: ", err);
                // Si el backend se nego (por ejemplo, es un parametro del
                // sistema) el motivo viene en detail.
                mensaje("error", err.response?.data?.detail || "No se pudo eliminar el parametro.");
                return false;
            });

        setParametroSeleccionado(null);

        if (!eliminado) return;

        mensaje("success", "Se elimino el parametro.");

        fetchResults(filtro);
    }

    const columns = [
        { field: 'id', headerName: 'ID', width: 50, minWidth: 45, sortable: true, headerAlign: 'center', align: 'center',
            renderCell: (p) => <span style={{ fontSize: '0.75rem', color: '#666', fontFamily: 'monospace' }}>{p.value != null ? p.value : p.row.id}</span> },
        {
            field: 'codigo', headerName: 'Codigo', width: 260, sortable: false, headerAlign: 'left',
            renderCell: (cellValues) => {
                return <Link to={'/configuracion/edit/' + cellValues.row.id} style={{ color: 'inherit' }}>{cellValues.row.codigo}</Link>;
            }
        },
        { field: 'descripcion', headerName: 'Descripcion', width: 340, sortable: false, headerAlign: 'left' },
        { field: 'valor', headerName: 'Valor', width: 140, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'usuarioRegistro', headerName: 'Registrado por', width: 140, sortable: false, headerAlign: 'center', align: 'center' },
        {
            field: 'eliminar', headerName: '', width: 70, sortable: false, headerAlign: 'center', align: 'center',
            renderCell: (cellValues) => {
                return (
                    <span>
                        <Tooltip title="Eliminar">
                            <IconButton size="small" disabled={isReadOnly}
                                onClick={() => {
                                    setParametroSeleccionado(cellValues.row);
                                    setOpenDialogEliminar(true);
                                }}>
                                <IconDelete fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </span>
                )
            }
        },
    ];

    return (
        <div>
            <Grid container>
                <Grid item>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Configuracion
                    </Typography>
                </Grid>
                <Grid container spacing={1} padding={"12px 0px 0px 0px"}>
                    <Grid item xs={9} sm={4}>
                        <TextField
                            name="filtro"
                            label="Buscar"
                            fullWidth
                            value={filtro}
                            onChange={(event) => setFiltro(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") fetchResults(filtro);
                            }}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={3} sm={1} display={"flex"} alignItems={"center"}>
                        <Button variant="outlined" fullWidth size="medium"
                            onClick={() => fetchResults(filtro)}>
                            <IconSearch />
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                            <DataGridBase
                                rows={results}
                                columns={columns}
                                loading={cargando}
                                disableRowSelectionOnClick={false}
                                onRowClick={(event) => { setParametroSeleccionado(event.row); }}
                                onRowDoubleClick={(event) => {
                                    if (isReadOnly) return;

                                    navigate("/configuracion/edit/" + event.row.id);
                                }}
                                toolbarButtons={[
                                    {
                                        text: "Nuevo",
                                        icon: <IconAddCircle />,
                                        disabled: isReadOnly,
                                        onClick: () => navigate("/configuracion/edit/0"),
                                    }
                                ]}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Grid>

            <DialogYesNo
                open={openDialogEliminar}
                titulo={"Eliminar Parametro"}
                texto={"Se eliminara el parametro "
                    + (parametroSeleccionado?.codigo || "")
                    + ". Si lo usa el sistema no se podra. Desea continuar?"}
                setYes={eliminar}
                setNo={() => { setParametroSeleccionado(null); }}
                openClose={() => {
                    setOpenDialogEliminar(!openDialogEliminar);

                    if (openDialogEliminar) setParametroSeleccionado(null);
                }}
            />

            <Snackbar open={openMessage} autoHideDuration={5000} onClose={() => setOpenMessage(!openMessage)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert onClose={() => setOpenMessage(!openMessage)} severity={propsMessage.severity}>
                    {propsMessage.message}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default Index;
